// The account menu behind the top-bar avatar (owner, 2026-10-25: rebuilt like the wallet and bell
// dropdowns). Who you are (name, email, role, two-step state), the business and store you are in
// (plan, switch store), your account and the settings people look for most, the theme as one
// three-way switch, and sign out. It also works outside the shell (the "set up a business" screen),
// where only the account part shows.
import { useContext, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { formatTime } from "@ady/format";
import { t } from "@ady/i18n";
import { cx, Icon, useToast, VerifiedTick, type IconName } from "@ady/ui";
import { api } from "../api/client";
import type { MeResponse } from "../api/types";
import { applyTheme, readTheme, type ThemeChoice } from "../theme";
import { TID } from "../testids";
import { AppLink, useGo } from "./AppLink";
import { ShellContext } from "./context";
import { can } from "./nav";
import { usePopover } from "./usePopover";
import { VerifiedMark, verificationHref } from "./VerifiedMark";

export function initials(name: string) {
  return (
    name
      .split(/\s+/)
      .map((p) => Array.from(p)[0] ?? "")
      .join("")
      .slice(0, 2)
      .toUpperCase() || "?"
  );
}

const THEMES: [
  ThemeChoice,
  IconName,
  "shell.themeSystem" | "shell.themeLight" | "shell.themeDark",
][] = [
  ["system", "monitor", "shell.themeSystem"],
  ["light", "sun", "shell.themeLight"],
  ["dark", "moon", "shell.themeDark"],
];

export function AccountMenu({ me }: { me: MeResponse }) {
  const shell = useContext(ShellContext);
  const { open, setOpen, ref } = usePopover();
  const [theme, setTheme] = useState<ThemeChoice>(readTheme);
  const qc = useQueryClient();
  const toast = useToast();
  const go = useGo();
  const close = () => setOpen(false);
  const signOut = async () => {
    try {
      await api.logout();
    } finally {
      qc.clear();
      toast.show({ tone: "success", title: t("auth.signedOut") });
      go("/sign-in");
    }
  };
  // A new email or phone user may have no display name yet: name the menu by their address.
  const name = me.user.display_name || me.user.email || me.user.phone || "";
  const twoStep = me.user.mfa.totp || me.user.mfa.passkeys > 0;
  const m = shell?.membership;
  const base = shell ? `/${shell.orgSlug}/${shell.storeSlug}` : null;
  const canSwitch = Boolean(shell) && (me.memberships.length > 1 || (m?.stores.length ?? 0) > 1);
  const links: { href: string; icon: IconName; label: string }[] = [
    { href: "/account/security", icon: "shield", label: t("shell.profileMenu.profile") },
    { href: "/account/sessions", icon: "device", label: t("security.sessionsTitle") },
    ...(base
      ? [
          {
            href: `${base}/settings/notifications`,
            icon: "bell" as IconName,
            label: t("shell.profileMenu.notifications"),
          },
          ...(m && can(m, ["team.read"])
            ? [
                {
                  href: `${base}/settings/team`,
                  icon: "users" as IconName,
                  label: t("shell.profileMenu.team"),
                },
              ]
            : []),
          ...(m && can(m, ["billing.read"])
            ? [
                {
                  href: `${base}/settings/billing`,
                  icon: "receipt" as IconName,
                  label: t("shell.profileMenu.billing"),
                },
              ]
            : []),
        ]
      : []),
  ];
  const row =
    "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-start text-xs text-fg no-underline hover:bg-hover";
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={t("shell.accountMenu", { name })}
        data-testid={TID.accountMenuButton}
        onClick={() => setOpen((o) => !o)}
        className="relative flex size-11 items-center justify-center rounded-full"
      >
        <span
          className={cx(
            "flex size-9 items-center justify-center rounded-full bg-action-bg text-sm font-semibold text-action-fg",
            open && "ring-2 ring-link ring-offset-2 ring-offset-surface",
          )}
          data-content="user"
        >
          {initials(name)}
        </span>
        {!twoStep ? (
          <span
            className="absolute end-1 top-1 size-2.5 rounded-full border-2 border-surface bg-warning-fg"
            aria-hidden
          />
        ) : null}
      </button>
      {open ? (
        <div
          role="dialog"
          aria-label={t("shell.accountMenu", { name })}
          data-testid={TID.accountMenu}
          className="absolute end-0 top-full z-(--ady-z-popover) mt-2 flex w-72 max-w-[calc(100vw-1rem)] flex-col overflow-hidden rounded-md border border-subtle bg-overlay shadow-2"
        >
          {/* Who you are */}
          <div className="ady-bg-brand-tint flex items-center gap-2.5 px-3 py-2.5">
            <span
              className="flex size-10 flex-none items-center justify-center rounded-full ady-bg-brand text-sm font-semibold text-on-primary"
              data-content="user"
            >
              {initials(name)}
            </span>
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="flex min-w-0 items-center gap-1">
                <span className="truncate text-sm font-semibold" data-content="user">
                  {name}
                </span>
                {/* D-068: the owner of a verified shop carries the tick. */}
                {m?.org.verified && m.role.key === "owner" ? (
                  <VerifiedTick label={t("shell.verified")} />
                ) : null}
              </span>
              <span className="truncate text-2xs text-fg-secondary">
                {me.user.email ?? me.user.phone}
              </span>
              <span className="mt-1 flex flex-wrap gap-1">
                {m ? (
                  <span className="rounded-full bg-surface px-1.5 py-px text-2xs font-semibold text-fg-secondary">
                    {m.role.name}
                  </span>
                ) : null}
                {twoStep ? (
                  <span className="inline-flex items-center gap-0.5 rounded-full bg-success-bg px-1.5 py-px text-2xs font-semibold text-success-fg">
                    <Icon name="shieldCheck" className="size-3" />
                    {t("shell.profileMenu.twoStepOn")}
                  </span>
                ) : (
                  <AppLink
                    href="/account/security"
                    onClick={close}
                    className="inline-flex items-center gap-0.5 rounded-full bg-warning-bg px-1.5 py-px text-2xs font-semibold text-warning-fg no-underline"
                  >
                    <Icon name="triangle" className="size-3" />
                    {t("shell.profileMenu.twoStepOff")}
                  </AppLink>
                )}
              </span>
            </span>
          </div>

          {me.support ? (
            <span className="flex items-center gap-1.5 border-b border-subtle bg-warning-bg px-3 py-1.5 text-2xs font-semibold text-warning-fg">
              <Icon name="eye" className="size-3" />
              {t("shell.profileMenu.support", { time: formatTime(me.support.expires_at) })}
            </span>
          ) : null}

          {/* Where you are */}
          {shell && m ? (
            <div className="flex items-center gap-2 border-b border-subtle px-3 py-2">
              <span className="flex size-7 flex-none items-center justify-center rounded-xs bg-sunken">
                <Icon name="store" gradient />
              </span>
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="flex min-w-0 items-center gap-1">
                  <span className="truncate text-xs font-semibold" data-content="user">
                    {shell.store.name}
                  </span>
                  <VerifiedMark
                    verified={m.org.verified}
                    href={verificationHref(shell.orgSlug, shell.storeSlug)}
                    onGo={close}
                  />
                </span>
                <span className="flex min-w-0 items-center gap-1 text-2xs text-fg-tertiary">
                  {m.org.name !== shell.store.name ? (
                    <span className="truncate" data-content="user">
                      {m.org.name} ·
                    </span>
                  ) : null}
                  <AppLink
                    href={`${base}/wallet`}
                    onClick={close}
                    className="inline-flex flex-none items-center gap-0.5 font-semibold text-link no-underline hover:underline"
                    title={t("shell.profileMenu.planTitle")}
                  >
                    <Icon name="crown" className="size-3" />
                    {m.plan.name}
                  </AppLink>
                </span>
              </span>
              {canSwitch ? (
                <button
                  type="button"
                  onClick={() => {
                    close();
                    shell.openStoreSwitcher();
                  }}
                  className="inline-flex flex-none items-center gap-1 rounded-xs border border-control px-2 py-1 text-2xs font-semibold text-fg-secondary hover:bg-hover"
                  data-testid="account-switch-store"
                >
                  <Icon name="swap" className="size-3" />
                  {t("shell.profileMenu.switch")}
                </button>
              ) : null}
            </div>
          ) : null}

          {/* Your account and the settings people look for most */}
          <nav className="flex flex-col p-1.5" aria-label={t("shell.profileMenu.links")}>
            {links.map((l) => (
              <AppLink key={l.href} href={l.href} onClick={close} className={row}>
                <Icon name={l.icon} gradient />
                <span className="min-w-0 flex-1 truncate">{l.label}</span>
                <Icon name="chevronRight" className="size-3.5 text-fg-tertiary" />
              </AppLink>
            ))}
          </nav>

          {/* Theme: one three-way switch */}
          <div className="flex items-center justify-between gap-2 border-t border-subtle px-3 py-2">
            <span className="text-xs text-fg-secondary">{t("shell.theme")}</span>
            <span
              role="radiogroup"
              aria-label={t("shell.theme")}
              className="inline-flex rounded-full bg-sunken p-0.5"
            >
              {THEMES.map(([choice, icon, label]) => {
                const on = theme === choice;
                return (
                  <button
                    key={choice}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    aria-label={t(label)}
                    title={t(label)}
                    onClick={() => {
                      setTheme(choice);
                      applyTheme(choice);
                    }}
                    className={cx(
                      "inline-flex items-center gap-1 rounded-full px-2 py-1 text-2xs font-semibold",
                      on ? "bg-surface text-fg shadow-1" : "text-fg-tertiary hover:text-fg",
                    )}
                    data-testid={`theme-${choice}`}
                  >
                    <Icon name={icon} gradient={on} className="size-3.5" />
                    {on ? t(label) : null}
                  </button>
                );
              })}
            </span>
          </div>

          <div className="border-t border-subtle p-1.5">
            <button
              type="button"
              data-testid={TID.signOut}
              onClick={signOut}
              className={cx(row, "text-danger-fg hover:bg-danger-bg")}
            >
              <Icon name="logout" />
              {t("common.signOut")}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
